import React, {useCallback, useEffect, useState} from "react";
import {useHistory, useParams} from "react-router-dom";
import QRCode from "react-qr-code";
import {getToken, saveToken, clearToken, demoFetch, post, startFreshDemo, page, card, greenBtn, primaryBtn, darkBtn, inputStyle, Portrait} from "./ui";
import {CharacterCreator} from "./character";
import {DrawPhase} from "./draw";
import {RevealPhase} from "./reveal";
import {GuessPhase} from "./guess";
import {ScoresPhase} from "./scores";
import {BumpPhase} from "./bump";
import {InterviewPhase} from "./interview";
import {RoastPhase} from "./roast";
import {ZedSays} from "./zed";
import {Tour} from "./tour";
import {FlyStage} from "./stage";
import {FlySwap} from "./fly";

const Title = () => (
    <img src="/party-machine-logo.png" alt="Party Machine"
         style={{display: 'block', width: '100%', height: 'auto', margin: '0 auto 10px', filter: 'drop-shadow(0 8px 24px rgba(0,0,0,0.5))'}} />
);

// /sell — the host's start page (the unshared URL). One tap mints a coded game
// and drops you straight into it as host.
export const SellHost = () => {
    const history = useHistory();
    const [busy, setBusy] = useState(false);
    const [err, setErr] = useState('');
    const start = async (mode) => {
        setBusy(true); setErr('');
        clearToken(); // start clean; no stale game to inherit
        const ok = await startFreshDemo(history, mode);
        if (ok !== true) { setErr(ok === 'resting' ? 'Zed is resting. Try again in a bit.' : 'Could not start'); setBusy(false); }
    };
    return <div style={{...page, justifyContent: 'center'}}>
        <Title />
        {err && <div style={{color: '#ff8a8a', textAlign: 'center', fontWeight: 700}}>{err}</div>}
        <button style={primaryBtn} disabled={busy} onClick={() => start('draw_off')}>{busy ? '…' : 'Start Demo'}</button>
        <button style={darkBtn} disabled={busy} onClick={() => start('kickoff')}>{busy ? '…' : 'Start Kickoff 👊'}</button>
    </div>;
};

// Top-level screen identity: a change flies the old screen out and the new one in.
const screenKey = (s) => {
    if (!s) return 'join';
    if (s.phase === 'lobby') {
        if (s.you.character_status === 'none') return 'char:form';
        // Building and the reveal are one screen (the portrait lands in place), so no page transition between
        // them. The Draw-Off host goes straight to the control surface.
        if (s.is_host && s.mode !== 'kickoff' && s.you.character_status === 'ready') return 'host-lobby';
        return 'char:becoming';
    }
    return s.phase;
};

// /demo/:code — the play URL. Resume if your token belongs to this game, else join it.
export const DemoGame = () => {
    const {code} = useParams();
    const history = useHistory();
    const [state, setState] = useState(null);
    const [offset, setOffset] = useState(0); // server_time - Date.now(), for the countdown
    const [needsJoin, setNeedsJoin] = useState(false);
    const [loading, setLoading] = useState(true);

    const poll = useCallback(async () => {
        if (!getToken()) { setNeedsJoin(true); setState(null); setLoading(false); return; }
        try {
            const res = await demoFetch(`/demo/state?code=${encodeURIComponent(code)}`);
            if (res.status === 404) { setNeedsJoin(true); setState(null); setLoading(false); return; }
            if (res.ok) {
                const s = await res.json();
                // The host started a fresh game ("Run it again"): go join it, as a new player.
                if (s.moved_to) { clearToken(); history.replace(`/demo/${s.moved_to}`); return; }
                setNeedsJoin(false); setState(s); setOffset(s.server_time - Date.now());
            }
        } catch (e) { /* keep last good state through a blip */ }
        setLoading(false);
    }, [code, history]);

    useEffect(() => {
        poll();
        const id = setInterval(poll, 1000);
        return () => clearInterval(id);
    }, [poll]);

    const onJoined = () => { setNeedsJoin(false); setLoading(true); poll(); }; // token saved; pick it up now

    if (loading && !state && !needsJoin) return <div style={{...page, alignItems: 'center', justifyContent: 'center'}}>…</div>;
    const joining = needsJoin || !state;
    return <FlyStage screenKey={joining ? 'join' : screenKey(state)}>
        {joining ? <JoinForm code={code} onJoined={onJoined} /> : <Phase state={state} offset={offset} history={history} code={code} onJoined={onJoined} />}
    </FlyStage>;
};

const Phase = ({state, offset, history, code, onJoined}) => {
    switch (state.phase) {
        case 'lobby': return <Lobby state={state} history={history} />;
        case 'draw': return <DrawPhase state={state} offset={offset} />;
        case 'guess': return <GuessPhase state={state} offset={offset} />;
        case 'reveal': return <RevealPhase state={state} offset={offset} />;
        case 'bump': return <BumpPhase state={state} offset={offset} />;
        case 'interview': return <InterviewPhase state={state} offset={offset} />;
        case 'roast': return <RoastPhase state={state} history={history} />;
        case 'scores':
        case 'done': return <ScoresPhase state={state} offset={offset} history={history} />;
        default: return <JoinForm code={code} onJoined={onJoined} />;
    }
};

// Guest entry for a specific game code: name + fact in one shot.
export const JoinForm = ({code, onJoined}) => {
    const [name, setName] = useState('');
    const [fact, setFact] = useState('');
    const [busy, setBusy] = useState(false);
    const [err, setErr] = useState('');
    const [ended, setEnded] = useState(false);
    const [step, setStep] = useState('name');
    const [asked, setAsked] = useState(null);
    const [named, setNamed] = useState(false);
    // like an answer: the button confirms, holds a beat, then the name sweeps off and the fact slides in
    const toFact = () => {
        if (named || !name.trim()) return;
        setNamed(true);
        setTimeout(() => setStep('fact'), 500);
    };

    const submit = async () => {
        if (!name.trim()) { setErr('Enter your name'); return; }
        if (!fact.trim()) { setErr('Add one fact about you'); return; }
        setBusy(true); setErr('');
        clearToken();
        try {
            const res = await post('/demo/join', {code, name: name.trim()});
            if (!res.ok) {
                const j = await res.json().catch(() => ({}));
                if (j.error === 'no_game') { setEnded(true); setBusy(false); return; }
                setErr(j.error === 'not_joinable' ? 'That game already started' : j.error === 'full' ? 'That game is full' : 'Could not join');
                setBusy(false);
                return;
            }
            const j = await res.json();
            saveToken(j.token);
            try { await post('/demo/character', {emblem: '', fact: fact.trim()}); } catch (e) { /* fallback form handles it */ }
            onJoined();
        } catch (e) {
            setErr('Network error');
            setBusy(false);
        }
    };

    if (ended) return <div style={{...page, justifyContent: 'center'}}>
        <Title />
        <div style={{textAlign: 'center', fontSize: 18, fontWeight: 700, opacity: 0.85}}>This game has ended.</div>
    </div>;

    // Zed asks one question at a time: his line types out, then that answer's box and button fade in, one after
    // the other. They keep their space while hidden so nothing jumps.
    const onName = step === 'name';
    const ready = asked === step;
    const fade = (i) => ({
        opacity: ready ? 1 : 0, transform: ready ? 'none' : 'translateY(8px)', pointerEvents: ready ? 'auto' : 'none',
        transition: ready ? `opacity 450ms ease ${i * 280}ms, transform 450ms ease ${i * 280}ms` : 'none',
    });
    return <div style={{...page, justifyContent: 'space-evenly'}}>
        <ZedSays line={onName ? 'What is your name?' : 'Tell me something about yourself.'} onDone={() => setAsked(step)} />
        <FlySwap swapKey={step}>
        <div style={{display: 'flex', flexDirection: 'column', gap: 12}}>
            <div style={fade(0)}>
                {onName
                    ? <input key="name" type="text" className="demo-input" style={inputStyle} value={name} onChange={e => setName(e.target.value)} placeholder="Your name"
                             onKeyDown={e => { if (e.key === 'Enter') toFact(); }} />
                    : <>
                        <input key="fact" type="text" className="demo-input" style={inputStyle} value={fact} onChange={e => setFact(e.target.value)} placeholder="One interesting fact"
                               onKeyDown={e => { if (e.key === 'Enter') submit(); }} />
                        <p style={{textAlign: 'right', margin: '8px 0 0', opacity: 0.6}}><i>e.g.</i> "I once ate 40 tacos"</p>
                    </>}
            </div>
            {err && <div style={{color: '#ff8a8a', textAlign: 'center', fontWeight: 700}}>{err}</div>}
            <div style={fade(1)}>
                {onName
                    ? <button style={{...primaryBtn, opacity: name.trim() ? 1 : 0.45}} disabled={!name.trim()} onClick={toFact}>{named ? '✓ Next' : 'Next'}</button>
                    : <button style={{...primaryBtn, opacity: fact.trim() && !busy ? 1 : 0.45}} disabled={busy || !fact.trim()} onClick={submit}>{busy ? 'Summoning…' : 'Join'}</button>}
            </div>
        </div>
        </FlySwap>
    </div>;
};

const Lobby = ({state, history}) => {
    const me = state.you;
    const [hostContinued, setHostContinued] = useState(false);
    const joinUrl = `${window.location.origin}/demo/${state.code}`;
    const qr = state.is_host ? <QrCard url={joinUrl} /> : null;
    const reset = () => startFreshDemo(history, state.mode);

    // Guests stay on their character (Zed sizing them up, then the reveal) until the game starts. The host
    // sees the same, then continues to the control surface. (In Draw-Off the host is hardcoded ready.)
    if (!state.is_host || (me.character_status !== 'ready' || !hostContinued) && state.mode === 'kickoff')
        return <CharacterCreator you={me} qr={qr} onContinue={state.is_host ? () => setHostContinued(true) : null} />;
    if (me.character_status !== 'ready') return <CharacterCreator you={me} qr={qr} />;

    // Host: the control surface.
    const enough = state.players.length >= 2 && state.all_ready;
    return <div style={page}>
        <QrCard url={joinUrl} />
        <Roster players={state.players} />
        <button style={enough ? greenBtn(false) : darkBtn} className={enough ? 'jfd-join-game-pulse' : undefined}
                disabled={!enough} onClick={() => post('/demo/start')}>
            {state.players.length < 2 ? 'Need 2+ players' : (state.all_ready ? 'Start Game' : 'Waiting for characters…')}
        </button>
        <button style={{padding: 10, fontSize: 14, fontWeight: 700, color: '#aaa', background: 'transparent', border: '1px solid rgba(255,255,255,0.22)', borderRadius: 8, cursor: 'pointer', fontFamily: 'inherit'}}
                onClick={reset}>Reset Demo</button>
    </div>;
};

const QrCard = ({url}) => (
    <div style={{...card, padding: 16, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 14}}>
        <div style={{fontWeight: 800, letterSpacing: 1}}>SCAN TO JOIN</div>
        <div style={{background: '#fff', padding: 12, width: '100%', boxSizing: 'border-box'}}>
            <QRCode value={url} size={320} style={{width: '100%', height: 'auto', display: 'block'}} />
        </div>
    </div>
);

const Roster = ({players}) => (
    <div style={{display: 'flex', flexDirection: 'column'}}>
        <div style={{fontWeight: 800, marginBottom: 6}}>Players ({players.length})</div>
        {players.map(p => (
            <div key={p.token} style={{display: 'flex', alignItems: 'center', gap: 16, padding: '16px 0', borderTop: '1px solid rgba(255,255,255,0.12)'}}>
                <Portrait player={p} size={96} />
                <div style={{flex: 1, minWidth: 0}}>
                    <div style={{fontSize: 30, fontWeight: 800}}>{p.name}</div>
                    <div style={{fontSize: 18, fontStyle: 'italic', opacity: 0.6}}>{p.nickname || (p.portrait_status === 'ready' ? '' : 'summoning…')}</div>
                </div>
                <div style={{fontSize: 26}}>{p.portrait_status === 'ready' ? '✓' : '…'}</div>
            </div>
        ))}
    </div>
);

export default DemoGame;

// /demo-preview/:screen: one screen with stand-in data, for iterating on the look without playing a game.
export const DemoPreview = () => {
    const {screen} = useParams();
    switch (screen) {
        case 'host': return <CharacterCreator you={{name: 'Cole', character_status: 'none'}} qr={null} />;
        case 'building': return <BuildingPreview />;
        case 'tour': return <Tour />;
        case 'zed': return <div style={page}><ZedSays line={new URLSearchParams(window.location.search).get('line') || 'What is your name?'} /></div>;
        case 'join':
        default: return <JoinForm code="preview" onJoined={() => {}} />;
    }
};

// Character building with the data arriving on a realistic schedule: reaction ~2s, nickname ~3s, portrait ~9s.
const BuildingPreview = () => {
    const [you, setYou] = useState({name: 'Tessa', character_status: 'processing'});
    useEffect(() => {
        const t = [
            setTimeout(() => setYou(y => ({...y, zed_reaction: 'A marathon. Untrained. For money. Noted.'})), 2000),
            setTimeout(() => setYou(y => ({...y, nickname: 'Blister Capitalist'})), 3000),
            setTimeout(() => setYou(y => ({...y, character_status: 'ready', portrait_url: '/demo-preview/portrait.webp'})), 9000),
        ];
        return () => t.forEach(clearTimeout);
    }, []);
    return <CharacterCreator you={you} qr={null} />;
};
